import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Building2, MessageSquare, Bot, Phone, Users, Workflow, ChevronDown, ChevronUp, CheckCircle2, ArrowRight, Loader2 } from 'lucide-react';

// "Primeros pasos": hila los 6 pasos esenciales de config (que hoy viven en ~6 menus distintos)
// en un checklist con estado por color + deep-link al panel que YA existe. No reconstruye nada.
// EXCLUSIVO del administrador del tenant (nadie abajo lo ve). El estado lo calcula UNA sola
// fuente de verdad en el backend (/onboarding/status), que lee las colecciones reales y respeta
// el gating del modulo de Planes (voz/asistente -> 'gated' si el plan no lo incluye).
const LS_KEY = 'onboarding_checklist_collapsed';

const STEPS = [
  { key: 'negocio',   n: 1, Icon: Building2,    title: 'Tu negocio y horario',        hint: 'Nombre, horario y zona horaria',            to: '/settings?tab=general' },
  { key: 'whatsapp',  n: 2, Icon: MessageSquare, title: 'Conectar WhatsApp',           hint: 'Para que la IA reciba y conteste',          to: '/whatsapp' },
  { key: 'asistente', n: 3, Icon: Bot,          title: 'Encender el Asistente IA',    hint: 'Un interruptor y su nombre',                to: '/settings?tab=ia' },
  { key: 'voz',       n: 4, Icon: Phone,        title: 'Voz y recepcion telefonica',  hint: 'Numero y recepcion de llamadas',            to: '/settings?tab=integraciones' },
  { key: 'equipo',    n: 5, Icon: Users,        title: 'Tu equipo y la ruleta',       hint: 'A quien se asignan los leads',              to: '/team-config' },
  { key: 'reglas',    n: 6, Icon: Workflow,     title: 'Reglas de comunicacion',      hint: 'El seguimiento automatico (opcional)',      to: '/logicas-recetas' },
];

function Pill({ st }) {
  if (st === 'ok')      return <span className="text-[10px] font-semibold px-2 py-0.5 rounded-md bg-emerald-500/15 text-emerald-400 border border-emerald-500/20">Listo</span>;
  if (st === 'gated')   return <span className="text-[10px] font-semibold px-2 py-0.5 rounded-md bg-white/5 text-muted-foreground border border-white/10">No incluido en tu plan</span>;
  if (st === 'unknown') return <span className="text-[10px] font-semibold px-2 py-0.5 rounded-md bg-white/5 text-muted-foreground border border-white/10">Estado desconocido</span>;
  return <span className="text-[10px] font-semibold px-2 py-0.5 rounded-md bg-amber-500/15 text-amber-400 border border-amber-500/20">Falta</span>;
}

export default function OnboardingChecklist() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';   // exclusivo del admin del tenant; nadie abajo
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  // Siempre arranca CONTRAIDO. Solo queda expandido si el admin lo abrio a proposito antes.
  const [collapsed, setCollapsed] = useState(() => { try { return localStorage.getItem(LS_KEY) !== '0'; } catch (e) { return true; } });

  useEffect(() => {
    if (!isAdmin) return;   // no consultamos para roles que no deben verlo
    let alive = true;
    (async () => {
      try {
        const res = await api.get('/onboarding/status');
        if (alive) setStatus(res?.data?.steps || null);
      } catch (e) {
        if (alive) setStatus(null);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [isAdmin]);

  const toggle = () => setCollapsed((c) => { const n = !c; try { localStorage.setItem(LS_KEY, n ? '1' : '0'); } catch (e) {} return n; });

  if (!isAdmin) return null;                  // nadie por debajo de admin ve este menu
  if (loading) {
    return (
      <Card className="mb-4 p-4 bg-card/50 border-white/10 flex items-center gap-2 text-xs text-muted-foreground" data-testid="onboarding-checklist-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> Revisando la configuracion de tu sistema...
      </Card>
    );
  }
  if (!status) return null;                    // si el backend fallo, no mostramos un checklist roto

  const applicable = STEPS.filter((s) => status[s.key] !== 'gated');
  const done = applicable.filter((s) => status[s.key] === 'ok').length;
  const total = applicable.length;
  const pct = total ? Math.round((done / total) * 100) : 0;
  const allDone = total > 0 && done === total;

  return (
    <Card className="mb-4 bg-card/50 border-white/10 overflow-hidden" data-testid="onboarding-checklist">
      <button type="button" onClick={toggle} className="w-full flex items-center gap-3 p-4 text-left hover:bg-white/[0.02] transition-colors" data-testid="onboarding-toggle">
        <div className="shrink-0">
          {allDone
            ? <span className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-emerald-500/15 text-emerald-400"><CheckCircle2 className="w-5 h-5" /></span>
            : <span className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-primary/15 text-primary font-bold text-sm">{done}/{total}</span>}
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-white">{allDone ? 'Tu sistema esta listo' : 'Primeros pasos'}</p>
          <p className="text-[11px] text-muted-foreground">{allDone ? 'Todo lo esencial esta configurado.' : `Configura tu sistema — ${done} de ${total} listos`}</p>
          {!allDone && <div className="mt-2 max-w-xs"><Progress value={pct} className="h-1.5" /></div>}
        </div>
        <span className="shrink-0 text-muted-foreground">{collapsed ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}</span>
      </button>

      {!collapsed && (
        <div className="px-4 pb-4 space-y-2" data-testid="onboarding-steps">
          {STEPS.map((s) => {
            const st = status[s.key];
            const Icon = s.Icon;
            return (
              <div key={s.key} className="flex items-center gap-3 rounded-lg border border-white/5 bg-black/20 p-2.5" data-testid={`onboarding-step-${s.key}`}>
                <span className={`shrink-0 inline-flex items-center justify-center w-8 h-8 rounded-lg ${st === 'ok' ? 'bg-emerald-500/10 text-emerald-400' : st === 'gated' || st === 'unknown' ? 'bg-white/5 text-muted-foreground' : 'bg-amber-500/10 text-amber-400'}`}>
                  <Icon className="w-4 h-4" />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-medium text-white flex items-center gap-1.5"><span className="text-[10px] text-muted-foreground font-mono">{s.n}.</span>{s.title}</p>
                  <p className="text-[10px] text-muted-foreground truncate">{s.hint}</p>
                </div>
                <Pill st={st} />
                {st !== 'gated' && (
                  <Link to={s.to} className="shrink-0 inline-flex items-center gap-1 text-[11px] font-medium text-primary hover:text-primary/80 px-2 py-1 rounded-md hover:bg-primary/10 transition-colors" data-testid={`onboarding-cta-${s.key}`}>
                    {st === 'ok' ? 'Revisar' : 'Configurar'} <ArrowRight className="w-3 h-3" />
                  </Link>
                )}
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}
